import { useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { selectLearningModuleById } from 'src/redux/slices/learningModules';
import { useGetLearningModuleQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { LearningModuleStateData } from 'src/types/state/learningModule';

/**
 * Hook to get learning module data with automatic full data fetching
 *
 * This hook:
 * 1. Gets module from Redux store (may be preview data from topic/search APIs)
 * 2. Checks if full data is available (has instanceMap with content URLs)
 * 3. Automatically fetches full data via GetModule API if needed
 * 4. Returns the most complete data available
 *
 * @param learningModuleId - The learning module ID to fetch
 * @returns Object containing module data, loading state, error state, and existence check
 */
const useLearningModule = (learningModuleId: string | undefined) => {
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const moduleFromStore = useAppSelector((state: AppState) => (
    learningModuleId ? selectLearningModuleById(state, learningModuleId) : undefined
  ));

  const hasFullData = (moduleData?: LearningModuleStateData) => {
    return moduleData?.instanceMap && Object.keys(moduleData.instanceMap).length > 0;
  };

  // When selLocale is set, always fetch — the Redux store is keyed by moduleId only,
  // so stored data may be from a different locale. RTK Query deduplicates by
  // (moduleId, clientId, selLocale) cache key, so no redundant network calls.
  const shouldFetch = !!learningModuleId && (!!selLocale || !hasFullData(moduleFromStore?.data));

  const {
    isLoading,
    isError,
    error,
  } = useGetLearningModuleQuery(
    { moduleId: learningModuleId!, clientId, selLocale },
    { skip: !shouldFetch },
  );

  return {
    /**
     * Always return data from Redux store (never directly from API)
     * The API call populates the store, then we read the shaped data.
     * When selLocale is set and loading, return undefined to avoid showing stale locale data.
     */
    learningModule: (selLocale && isLoading) ? undefined : moduleFromStore?.data,

    /**
     * Loading state - true when fetching full data
     */
    isLoading,

    /**
     * Error state from GetModule API call
     */
    isError,

    /**
     * Error object from GetModule API call
     */
    error,
  };
};

export default useLearningModule;
